// The postMessage mirror of the runtime for the editor frame (WS-49, contract section 4; shapes: settings/typedefs.js
// `RuntimeMessage`). The editor (app host) frames the map page and drives it with { mms: 1, type, id, ... }.
// Origin-checked against the allowlist of the boot record, in both directions, and never '*': a message is read
// only when it comes from the framing window on an allowed origin, and nothing is posted before such a message
// has named the origin to answer. Not framed, or no allowlist = inert (returns null, no listener).
/** @typedef {import('./settings/typedefs.js').Runtime} Runtime */
/** @typedef {import('./settings/typedefs.js').View} View */
/** @typedef {import('./settings/typedefs.js').Translatable} Translatable */

// ---------------------------------------------------------------------------------------------------------------
// EDITOR MODE (WS-51 interface commit, contract section 4 "Editor mode"). The messages the editor of delivery 3
// sends ON TOP OF the runtime mirror above, and the events it gets back. INTERFACE: names and shapes are fixed here
// and mirrored for the portal in apps/portal/src/editor/bridge.d.ts (tests/unit/runtime/bridge-interface.test.mjs
// keeps the two and the contract equal). Track T3 writes the bodies; a message that is typed here and not built
// yet is answered `error` `not-implemented`, so the editor fails fast instead of waiting for an answer.
// Nothing about origins changes: same allowlist, same framing window, never '*'. These messages exist only where
// the boot record names an editor origin, which is the draft page of the app host.
//
// Every request is `{ mms: 1, type, id, ...fields }` and is answered `{ mms: 1, type: 'result', id, value }` or
// `{ mms: 1, type: 'error', id, message }`. Every event is `{ mms: 1, type: 'event', id: '', event, payload }`.
// Positions on screen are CSS pixels from the top left corner of the FRAME (the page's own viewport), so the editor
// adds the frame's offset and nothing else. Positions on the map are the map's local metres (x east, z south).
//
//   REQUEST        FIELDS                                   VALUE                       WHAT IT DOES
//   hello          -                                        BridgeHello                 the first message: what this page is and which messages it implements
//   enterEditor    { ghosts?, pick? }                       EditorState                 editor mode on (below)
//   leaveEditor    -                                        EditorState                 back to the plain draft page
//   visitorPreview { on }                                   EditorState                 "Preview as visitor": every editor affordance off, editor mode kept
//   pick           { mode, once? }                          { mode }                    arms picking; each press on the map then comes back as the event `pick`
//   select         { key, fly?, card? }                     { key }                     selects a place (null = none) as if it had been pressed
//   locate         { place } | { point }                    { screen, visible, label? } where a place or a map point is on screen now; VIS-73: for a place also `label`, its
//                                                                                       name or number as drawn now: { drawn, rect } (drawn false: off, out of view or given way)
//   highlight      { target }                               { rect }                    outlines the control or place being edited (null = clear)
//   listControls   -                                        ControlRect[]               every control with its rectangle on screen
//   places         -                                        PlaceInfo[]                 every place and unit type of the map, for lists and pickers
//   capture        { still? }                               Capture                     the runtime's capture() plus the selection and, when asked, a still image (of `still.view` when given)
//   reload         { keep? }                                { reloading: true }         reloads the page for `rebuild` keys, keeping view, time and selection
//
//   EVENT          PAYLOAD                                  WHEN
//   pick           PickResult                               a press on the map while picking is armed
//   controls       ControlRect[]                            the rectangles changed (resize, a panel folded, `show` changed)
//   view           { moving }                               the camera started or stopped moving (hide what hangs on a point, then `locate` again)
//   undo           { redo }                                 (2026-10-20) Ctrl/Cmd+Z (`redo` false), Ctrl/Cmd+Shift+Z or Ctrl+Y (true) pressed in this page in editor
//                                                           mode, not while typing and not mid-gesture: the editor runs its own Undo / Redo
//   menu           { control, screen }                      (VIS-73) a right press on a visitor control in editor mode (its own menu stays shut): the
//                                                           editor opens that control's editor there (the date shortcuts: their card)
//   and the runtime's five, two of them with more in the payload:
//   ready          { values, shown, editor, restored }      `restored`: this load came from `reload` and view, time and selection are back
//   select         { key, screen, source }                  `screen`: where the selected place is; `source`: `visitor` (pressed on the map) or `editor` (the `select` request)
//
// EDITOR MODE, exactly:
//   - a press on a place selects it and sends `select`; it opens no booking link, starts no 360 tour and leaves the page nowhere;
//   - statistics are never sent (the draft page has no beacon; editor mode would stop them anyway);
//   - with `ghosts`, a control the preset HIDES is still drawn, dimmed, so that it has a rectangle for the editor's
//     Shown / Hidden tag ("hidden is not rendered" of contract section 3 holds for visitors; a ghost is an editor
//     affordance and is gone in `visitorPreview` and outside editor mode). VIS-73: it can be pressed and works as the
//     shown control would (its press reaches its settings: resolve `host.ghosts`), and a place the preset hides
//     stays on the map, faded, and can be pressed too;
//   - the editor's unsaved values are layer 9 (`set` with source `editor`), as before;
//   - a gallery item of that layer may carry `src` (an address on this origin) beside `asset`, so a photo uploaded a
//     moment ago shows before the next bundle names it. `src` is the editor's own and is never saved.
// `visitorPreview { on: true }`: ghosts, outlines and picking are off and the page behaves as the visitor's page of
// the same values (layer 9 included). The DEVICE is not a message: the portal sizes the frame, the page lays itself out.
// `reload`: the page keeps what it needs in its own session storage (the editor's layer 9, the editor state, the
// view, the time on screen, the selection), reloads, reads it once and deletes it; the next `ready` carries
// `restored: true`. Honoured only on a page whose boot record names an editor origin.

/** A point on screen: CSS pixels from the top left corner of the frame. @typedef {{ x: number, y: number }} ScreenPoint */
/** A rectangle on screen, same frame of reference. @typedef {{ x: number, y: number, w: number, h: number }} ScreenRect */
/** A point of the map in local metres (x east, z south); `y` is the ground height there, when known. @typedef {{ x: number, z: number, y?: number }} MapPoint */
/** What a press on the map picks: `place` a place or unit, `point` a spot of the ground, `feature` one of the client's own pins, lines and areas (delivery 4), `any` whichever is under the pointer, `off` nothing. @typedef {'off' | 'place' | 'point' | 'feature' | 'any'} PickMode */

/**
 * @typedef {Object} BridgeHello
 * @property {2} protocol              1 = the runtime mirror of delivery 1 alone; 2 = with the editor messages
 * @property {string} map              `map_...`
 * @property {string} preset           the preset on screen (`pre_...`)
 * @property {string} resort           the package id
 * @property {string[]} implements     the request types this page answers (everything else is `not-implemented`)
 * @property {EditorState} editor
 */

/**
 * @typedef {Object} EditorState
 * @property {boolean} editor    editor mode is on
 * @property {boolean} preview   "Preview as visitor" is on
 * @property {boolean} ghosts    hidden controls are drawn as ghosts
 * @property {PickMode} pick
 */

/**
 * The payload of the event `pick`.
 * @typedef {Object} PickResult
 * @property {string | null} place    the place or unit key under the pointer, or null (bare ground, or mode `point`)
 * @property {MapPoint | null} point  where the press meets the ground, or null when it meets nothing (the sky)
 * @property {ScreenPoint} screen     where the press was
 * @property {string | null} [feature]  delivery 4: the client feature (`ftr_...`) under the pointer in mode `feature` or `any`, else null
 */

/** What `highlight` outlines: a visitor control, a place, or the control that owns a setting. @typedef {{ control: string } | { place: string } | { setting: string }} HighlightTarget */

/**
 * One visitor control as the page draws it (`listControls`, the event `controls`).
 * @typedef {Object} ControlRect
 * @property {string} id               control id of the settings list
 * @property {boolean} shown           the preset shows it (as `runtime.controls()`)
 * @property {ScreenRect | null} rect  where it is; null = not on the page (hidden without ghosts, or its data is missing)
 * @property {boolean} ghost           drawn only as an editor ghost
 * @property {string | null} parent    the control it is a part of
 * @property {string} slot             the layout slot it sits in
 */

/**
 * One place or unit type of the map (`places`): what the Content tool lists and the place picker offers.
 * @typedef {Object} PlaceInfo
 * @property {string} key
 * @property {'place' | 'unitType' | 'unit' | 'building'} kind   WS-55 `building`: a building of the scene with no text (an
 *                                      unnamed one answers to `mtk-<id>`)
 * @property {'scenery' | 'named' | 'marked'} [shows]  WS-55: how the content shows it when no preset list moves it
 *                                      (content.js placeLevel): a unit or a place with a label marked, another place named
 * @property {string | null} type       unit: its unit type key
 * @property {string | null} category   category id of the content
 * @property {Translatable | null} title  as the content has it (the editor reads it in its own language)
 * @property {boolean} listed           it is in the explore list (what the readiness rules R1 and R2 look at)
 * @property {boolean} inSeason         it exists in the season on screen
 */

/**
 * A still image of what is on screen, asked for with `capture { still }`: the picture on a preset's card.
 * @typedef {Object} StillSpec
 * @property {number} width             pixels (the API stores 4:3; UploadRules.thumbnail)
 * @property {number} height
 * @property {'image/webp' | 'image/jpeg' | 'image/png'} [type]  default `image/webp`
 * @property {number} [quality]         0..1, default 0.85
 * @property {boolean} [chrome]         true = with the visitor's interface drawn over the map; default false: the map alone
 * @property {View} [view]              (VIS-248) the picture from this camera pose instead of the one on screen (a saved
 *                                      opening view or place angle, `camera.view` / `camera.places`): drawn and read back in
 *                                      one task, the pose on screen put back before the page paints, so nothing moves.
 *                                      Not an object: `bad-view`; positions that are not three numbers: `bad-view`
 */

/**
 * The value of `capture`: the runtime's `{ view, values }` and what the editor needs beside them.
 * @typedef {Object} Capture
 * @property {View} view
 * @property {{ [key: string]: any }} values
 * @property {string | null} selection  the selected place or unit key
 * @property {{ blob: Blob, width: number, height: number, type: string }} [still]  when `still` was asked for (a Blob crosses postMessage by structured clone)
 */

/** The request types of editor mode, beside the runtime mirror's `get`, `set`, `loadPreset`, `controls`, `capture`. */
export const EDITOR_REQUESTS = Object.freeze(['hello', 'enterEditor', 'leaveEditor', 'visitorPreview', 'pick', 'select', 'locate', 'highlight', 'listControls', 'places', 'reload']);
/** The events of editor mode, beside the runtime's five. */
export const EDITOR_EVENTS = Object.freeze(['pick', 'controls', 'view', 'undo', 'menu']); // (2026-10-20: + undo; VIS-73: + menu)
// ---------------------------------------------------------------------------------------------------------------

// ---------------------------------------------------------------------------------------------------------------
// THE TOOLS' MESSAGES (WS-52 interface commit, contract section 4 "The tools' messages"). What the tour builder,
// the layer editor and the notes to the studio of delivery 4 ask of the page ON TOP OF editor mode, and the events
// they get back. Same envelope, same origins, the same mirror file and the same test as above. Track T2 writes the
// bodies: ONE function per request, named as the request, in `EditorHost.tools` (main.js). Each takes the fields
// of the message and returns the value, or a promise of it. A request whose function is missing answers
// `not-implemented` and is left out of `hello`; nothing in this file knows what a function does.
// THE CODE BEHIND THEM IS NOT PART OF THE ALWAYS-LOADED ENGINE: it lives in web/js/lazy/ (layers.js, draw.js,
// marks.js) and a tool function imports its module when it is first asked (scripts/ci/check-assets.mjs budgets
// that folder apart). This file carries the names and the envelope only.
//
//   REQUEST          FIELDS                          VALUE                 WHAT IT DOES
//   drawStart        { kind, style? }                DrawState             arms a new point, line or area: each press on the ground adds a vertex (a point ends with its first press)
//                                                                          (WS-56) a right-click without a drag ends it like `drawFinish` (a right-drag still turns the camera);
//                                                                          a press on the first vertex of 3 or more closes an area, or a line into a loop, and ends it
//   drawVertex       { point } | { center: true }    DrawState             a vertex without a press, the keyboard's way: adds one at a point or in the middle of the view;
//                    { index, point | remove }                             with `index` it moves that vertex to `point`, or takes it away (`remove: true`)
//   drawUndo         -                               DrawState             takes the last step back (a vertex added, moved or removed)
//   drawFinish       -                               DrawResult            ends the shape and sends `draw-end`. Error `too-few`: a line needs 2 vertices, an area 3
//   drawCancel       -                               DrawState             drops the shape; `draw-end` says `cancelled`
//   drawEdit         { feature, geom, style? }       DrawState             an existing shape with a handle on every vertex: drag one, press the line between two to add
//                                                                          one; then `drawFinish` or `drawCancel`. That feature is not drawn twice while it is edited.
//                                                                          (WS-56) A right-click without a drag ends it like `drawFinish`; a loop keeps its closing pair
//   selectFeature    { feature, fly? }               { id }                outlines a client feature (null = none) as if it had been pressed; `fly` turns the camera to it
//   notePins         { pins, selected? }             { shown }             draws the pins of the notes to the studio: the WHOLE list each time ([] takes them away)
//                                                                          (WS-56: a pin with a `geom` is a route or an area, drawn dashed on the ground)
//   noteDraft        { point } | { arm: true }       NotePinEvent | null   the pin of the note being written: put it at a point (`point: null` takes it away), or let the
//                                                                          next press on the map put it. Once it stands it can be dragged; every move is the event `note-pin`
//   hereMarker       { place, point?, edit?, look? } | { point }  { screen, visible }   shows the "you are here" marker at a place or a map point (`place: null` takes it away)
//                                                                          (WS-91) a place AND a point: at the point, named by the place (a sign's fine-tuned spot);
//                                                                          `edit: true` looks straight down on it, lets it be dragged or put by a press
//                                                                          (each move: the event `here-move`); `edit: false` ends that
//                                                                          (VIS-227) `look: 'arrive'`: a flag where guests arrive at the place (Fine-tune Buildings), the camera stays;
//                                                                          `look: 'pin'`: the place's pin being moved (Fine-tune Life, a ride's pin), the same way
//   tourPreview      { tour, from? }                 TourPreviewState      plays a tour from a document that is NOT saved, from its first stop or from the stop `from`
//   tourPreviewStop  { tour, stop, hold?, look? }    TourPreviewState      plays ONE stop of it: the flight, its changes and its stay. A step at the same place (`at.same`)
//                                                                          starts from what the steps before it leave on screen (their changes are applied as a cut).
//                                                                          VIS-78 `hold`: afterwards the map stays as the stop left it until `tourStop`; `look`: no
//                                                                          flight and no move, only the stop's changes, at once (the editor shows a change it made)
//   tourStop         -                               TourPreviewState      stops what is playing; the tour's changes leave the map
//   tourTextSample   { on, caption, look? }          { shown }             VIS-82: shows `caption` ({ title, text }) as a stop's short text, without a tour;
//                                                                          VIS-226: look = the open tour's text look; `shown` false = no caption or a lobby panel
//   ---- fine-tune (WS-58, typed in apps/portal/src/editor/bridge.d.ts; lazy/finetune.js) ----
//   fineTune         { on, tool, view? }             { on, tool }          fine-tune on/off; `tool` lights | buildings | fences | trees | paths | life | select | null. On: a
//                                                                          straight-down view to start; (WS-89) right-drag orbits down to 20°, `view: 'top'` (key T) glides back. Off: the previous view back
//                                                                          (WS-89 follow-up: "straight down" = 89.5°, the heading kept; no sky view while on)
//   fineTouches      { touches, compare? }           { applied: true }     the portal's whole draft document (the setting `touches`), shown live; `compare: true`
//                                                                          shows the original until `compare: false`
//   fineList         -                               FineInventory         everything the tools can touch, as the map was built (before touches)
//   fineSelect       { ids, fly? }                   { ids }               the selection the panel made; the page highlights it. `fly` (a choice in the
//                                                                          panel's list): the straight-down view flies over it, near enough to fill a good part
//                                                                          (with the Trees tab, the id `strokes` = the area of the document's brush strokes;
//                                                                          (VIS-241) with the Paths tool ("Paths & areas", the places): a drawn path its bare id,
//                                                                          a way `way:<id>|<part>`, a resort ride's own route `route:<ride id>`, an area `area:<id>`
//                                                                          (a new route or area pick frames it); with the Life tool (the activities, framed on a
//                                                                          new pick): `ride:<id>` (a package ride or an added one: the line it runs on),
//                                                                          `walk:<id>` (walkers: their line), `herd:<paddock id>`, `wander:<id>`, `animals:<id>`
//                                                                          (their area, filled), (piece 3) `sled:<id>`, (piece 4) `between:<id>`: a pair going
//                                                                          there and back frames its way, (WS-89) `herd:made:<f-id>`:
//                                                                          a made enclosure's herd frames that fence's area); (WS-89 follow-up) Fences: `line:<fence>` the whole
//                                                                          fence (the set), `line:<fence>|<side>` one stretch (what a press on the map picks), `|<part>` one piece
//   fineBrush        { size, hide, soft, mode, thick }  { size, hide, soft, mode, thick, planted }  the tree brush (`size` metres across; `hide` false = bring trees back;
//                    VIS-199: `mode` 'hide' | 'back' | 'plant', `thick` 0-1: how thick planting is, as a share of the forest around;
//                    `planted` { shown, over }: the planted trees shown, over = more than the editor has room for)
//   fineArm          { what, kind?, ride?, finish? }  { what }             arms adding: `light` (next press places one), `path` (presses; a right-click, a
//                    { made?, undo? }                                     (WS-84: `made` = { side, offset } of a made fence being drawn, `undo` its last anchor back)
//                                                                          double-click or Enter ends; `kind` = path kind), `fence` (`kind` = paddock id), (WS-69)
//                                                                          `route` (`ride` = the ride's id: its route traced point by point, ended like a path; a
//                                                                          last press back on the first point makes it a loop; VIS-241: ends as `fine-touches`
//                                                                          with routes[ride] and `route:<ride>` picked), (WS-71) `area` (`kind` = the new
//                                                                          area's id: its outline drawn as a fence's; ends as `fine-touches` with
//                                                                          life.areas[kind] = { polygon, src: 'draw' } and `area:<kind>` picked), (piece 3) `sledline`
//                                                                          (`kind` = the hill's id: its line drawn from the top down as a path, >= 2 points;
//                                                                          ends as `fine-touches` with life.sled[kind].line and the hill picked, `sled:<id>`);
//                                                                          (piece 4) `spot` (`kind` = 'between:<id>:a' | 'between:<id>:b': the next press on the
//                                                                          map sets that end, `fine-touches` with life.between[id].a|b = { at }, the pair picked);
//                                                                          (ice fishing) `spot` with `kind` = 'fish:<id>': the next press puts that fishing spot
//                                                                          there, `fine-touches` with life.fish[id].at, the spot picked (`fish:<id>`);
//                                                                          null disarms, dropping what is being drawn, or with `finish` (the panel's Stop) ending it;
//                                                                          (WS-84) `made` (`kind` = the new fence's id `f-<6>`, `made` = { side, offset }: presses
//                                                                          on a road or path anchor to it, others are free corners, a press on the first anchor
//                                                                          closes it; `undo` takes the last anchor back; ends as `fine-touches` with made[kind]
//                                                                          and the fence picked as `made:<id>`), `gate` (`kind` = a made fence: the next press on
//                                                                          it adds a gate there)
//   fineMade         -                               FineMadeInfo[]        (WS-84) every made fence of the document shown, as the map resolves it
//   fineLife        -                               FineLife              (WS-69 Life) the map's origin and every ride: its route now (the points a person drags),
//                                                                          the package's named routes, speed, wait, direction, trail width; (VIS-241) the package's
//                                                                          and the added rides in one list, each with the line it runs on (`line`), `kind`, `built`,
//                                                                          `why`, `length`; + `walk` (FineWalk: walkers along a line, its length, loop, lost); (route
//                                                                          clearance) each ride and walkers + `hits` (FineHit[]: where its line runs into something hard). With the
//                                                                          `life` tool a ride is picked with `fineSelect` (ids = [`ride:<id>`]) or on the map (`fine-select`).
//                                                                          (WS-71) + areas (FineArea), herds (FineHerd), guests walking about (FineWander), places;
//                                                                          (piece 3) + sledding hills (FineSled; picked as `sled:<id>`); (piece 4) + the pairs going
//                                                                          there and back (FineBetween; picked as `between:<id>`, their ends dragged on the map)
//   fineGroup        { kinds?, turn?, move? }        { kinds, ids }        (WS-80 B, the `select` tool) `kinds`: the kinds a box picks (buildings, lights, fences, paths, ...;
//                                                                          a kind left out leaves the pick); `turn`: degrees clockwise about the pick's middle; `move`:
//                                                                          [dx, dz] metres. A turn or a move is one `fine-touches`. Picks are ids `<kind>:<id>` or
//                                                                          `<kind>:<id>#<i>,<j>` (some points of a line), with `fineSelect` and `fine-select` as the other tools
//   fineRoute        { from?, to? }                  FineRoute | null      walking directions tried: the route a guest is guided along from the place `from` to the
//                                                                          place `to` (keys) with the document shown, drawn on the map with Paths & areas and Buildings
//                                                                          until another pair (none: taken away); null when a place is not on the map
//   ---- render studio (WS-68, brief archive/docs-2026-10-21:docs/workstreams/WS-68-render-studio.md; lazy/renderstudio.js, editor only) ----
//   renderMode       { on, aspect?, fov? }           RenderModeState       render mode on/off. On: no interface, labels, pins, credits, hover, picking, selection
//                                                                          or highlight; orbit, pan, pinch and looking up at the sky stay; the frame of
//                                                                          `aspect` is shown (outside it dimmed); `fov` = the lens (degrees, 20..100). Off:
//                                                                          everything back, the previous lens too. Asked again while on: a new aspect or fov.
//                                                                          The wheel zooms as on the map (2026-10-17); the lens is ONE setting: the map's own
//                                                                          Ctrl + wheel / Ctrl + middle drag and the panel's slider (2026-11-01), every change
//                                                                          told as `render-view`; a `fov` within half a degree of the camera's is left alone;
//                                                                          on the map only (never in the images): a chip and a tips bar, its one button the
//                                                                          sky view's Look up (where the map has one)
//   renderPasses     { width? }                      RenderPasses          the frame, the class map and the depth map of this instant (wind and animals held
//                                                                          still), trees softened, `width` = the long side (512..2048 px, default 1600): three
//                                                                          Blobs, by structured clone. Error `not-rendering` outside render mode
//   ---- Film (VIS-265, brief docs/work/VIS-265-film-tool.md; lazy/film.js and lazy/filmshot.js, editor only, studio) ----
//   filmMode         { on, frame? }                  FilmModeState         Film has the map: the render studio's bare map, in the frame of `frame` ('9:16',
//                                                                          '9:8', '3:4', '4:5', '1:1', '16:9'); at night the northern lights show by
//                                                                          themselves. Off: the map's tilt limit, hour and lights come back
//   filmView         -                               FilmView              the view now ("Set point here"): camera, target, lens, the map's date, hour, lights
//   filmPlaceView    { place }                       FilmView              a view framing a place (`places` key) from the way the camera looks now; nothing moves
//   filmAim          { place }                       FilmPick              where a place is (centre, radius); a unit type: all its units together
//   filmRides        -                               { id, name, length, speed, loop, kind }[]  the rides a video can follow (their ride view); a shot's `ride`
//                                                                          then follows Film's clock (Film only: outside Film rides loop as always)
//   filmShow         { shot, t? }                    FilmTime              the map at second `t` of the shot (filmshot.js, a FilmShot); a playing shot stops.
//                                                                          Error `bad-shot` (no usable key), `not-filming` outside Film,
//                                                                          `season-unavailable` (the shot's season needs a reload; also filmPlay, filmRecord)
//   filmPlay         { shot, from? }                 FilmTime              plays the shot from second `from` in real time (`film-progress` follows it)
//   filmStop         -                               { t }                 the shot stops where it is (`t`: null when nothing played)
//   filmPhoto        { width? }                      FilmPhoto             the clean picture of this instant in the frame (JPEG Blob), `width` = the long side
//                                                                          (256..4096 px, default 1920)
//   filmRecord       { shot, fps?, size?, blur?, from?, to? }  FilmRecordStart  records the shot frame by frame into an MP4 (H.264), answered at
//                                                                          once; `film-record` follows it and carries the video at the end. `fps` 30 | 60 (default 30), `size` the
//                                                                          frame's short side 1080 | 2160 (default 1080), `blur` motion blur (default true). The
//                                                                          world advances exactly 1/fps a frame (animals, wind, clouds, aurora), however slow the
//                                                                          computer. `from`, `to`: only that part of the shot, in seconds (the timeline's in and
//                                                                          out points; default the whole shot). `filmStop` cancels it. Error `no-encoder` (the
//                                                                          browser cannot write H.264)
//
//   EVENT            PAYLOAD                         WHEN
//   draw             DrawState                       the shape being drawn or edited changed (a vertex was added, moved or taken away)
//   draw-end         DrawResult                      the shape was finished (its map coordinates) or cancelled
//   feature-select   FeatureSelect                   a client feature was pressed in editor mode, or `selectFeature` was asked
//   note-pin         NotePinEvent                    the draft pin was put or moved, or a note's pin was pressed
//   here-move        { point, place }                (WS-91) the "you are here" marker was dragged or put by a press while its spot is fine-tuned
//   tour-progress    TourProgress                    what a preview plays now: when a stop starts to fly, when it arrives, and when the playback is over
//   fine-touches     { touches, label }              (WS-58) a gesture on the map ended (drag released, stroke, path or outline finished, corner
//                                                    removed): the whole new document and a plain label; nothing is sent while dragging
//   fine-select      { tool, ids }                   (WS-58) the person selected on the map (press, Shift-press, rectangle)
//   fine-arm         { what: null }                  (WS-58) the page stopped adding by itself (a light placed, a path or an outline ended, Esc)
//   fine-menu        { tool, ids, x, y }             a right-click on a light in fine-tune: the panel opens its menu at x, y (the page's own pixels)
//   fine-remove      { tool, ids }                   Delete on picked buildings or props in fine-tune: the panel asks "Remove …?" and removes them on yes
//   fine-made-draft  FineMadeDraft                   (WS-84) a made fence being drawn changed (a press, Undo, a new side or offset, the pointer onto or off a way)
//   render-view      { fov }                         (WS-68) the lens changed on the map in render mode (whole degrees; 2026-11-01: by the map's own
//                                                    Ctrl + wheel / Ctrl + middle drag, Home, or anything else that sets the camera's lens)
//   film-progress    FilmProgress                    (VIS-265) a shot playing: about ten a second, and once when it ends (`playing: false`)
//   film-pick        FilmPick                        (VIS-265) a click (no drag) on the map in Film: the place under it, its centre and size
//   film-record      FilmRecordProgress              (VIS-265) a recording: frames done, about four a second; at the end `video` (or `error`)
//
// THE CLIENT'S FEATURES ARE SHOWN with the runtime's `set` (source `editor`): `layers.list`, `layers.features` and
// `layers.on` are live keys, so what the layer editor has not saved yet is drawn like everything else of layer 9.
// `pick { mode: 'feature' }` and `locate { feature }` are the two additions to the messages of delivery 3.
// RULES: every tool request needs editor mode (else the error `not-editing`). In "Preview as visitor" only the
// three tour requests and `hereMarker` are answered; entering it, or leaving editor mode, cancels a shape that is
// being drawn (`draw-end`, cancelled) and takes the note pins, the draft pin and the editor's marker off the map.
// A preview is never counted in the statistics and never loops; it uses the tour layer (8) like a real tour (whose
// keys win over the editor's unsaved values while it plays: settings/resolve.js RANK), so
// the preset's values are back when it ends. Coordinates are local metres, as everywhere.

/** One pair per vertex, local metres `[x east, z south]`: one for a point, two or more for a line, three or more for an area (not closed: the first is not repeated). (WS-56) A line closed into a loop repeats its first pair at the end, after three or more others. The shape of a feature's `geom` (contract section 7). @typedef {{ type: 'point' | 'line' | 'area', coords: [number, number][] }} FeatureGeom */

/**
 * The shape being drawn or edited (every draw request answers it; the event `draw`).
 * @typedef {Object} DrawState
 * @property {boolean} active     a shape is being drawn or edited
 * @property {'point' | 'line' | 'area' | null} kind
 * @property {string | null} id   the feature being edited (`drawEdit`); null for a new shape
 * @property {[number, number][]} coords  its vertices so far (a loop's last pair repeats its first: `drawVertex` indexes stop before it)
 * @property {boolean} canFinish  it has enough vertices
 * @property {boolean} canUndo
 */

/**
 * The value of `drawFinish` and the payload of the event `draw-end`.
 * @typedef {Object} DrawResult
 * @property {'point' | 'line' | 'area'} kind
 * @property {string | null} id   the feature that was edited; null for a new shape
 * @property {[number, number][]} coords  the finished vertices (what was there before, when a `drawEdit` is cancelled); a loop's last pair repeats its first
 * @property {boolean} cancelled
 * @property {number | null} length  metres along the ground: a line's length, an area's outline; null for a point
 * @property {number | null} area    square metres, an area only
 */

/**
 * The payload of the event `feature-select`.
 * @typedef {Object} FeatureSelect
 * @property {string | null} id      the client feature (`ftr_...`), or null: the selection was taken away
 * @property {string | null} layer   its layer (`lyr_...`)
 * @property {ScreenPoint | null} screen  where it is on screen (a line or an area: its middle)
 * @property {'visitor' | 'editor'} source  pressed on the map, or the `selectFeature` request
 */

/**
 * One note to the studio as a pin on the map (`notePins`).
 * @typedef {Object} NotePin
 * @property {string} id       `nte_...`
 * @property {string} label    what stands on the pin (a short number)
 * @property {'open' | 'in_progress' | 'done' | 'declined'} status  the pin says it with a mark, never with colour alone
 * @property {MapPoint | null} [point]  where the note was pinned; without one the pin stands at its `place`
 * @property {string | null} [place]    the place the note is about (a note written without the map names one from a list)
 * @property {FeatureGeom | null} [geom]  (WS-56) a route (`line`) or an area the note marks: drawn dashed in the pin's colour,
 *   the pin then at a route's first vertex or an area's centre
 */

/**
 * The payload of the event `note-pin` and the value of `noteDraft`.
 * @typedef {Object} NotePinEvent
 * @property {string | null} id      a note's pin, or null: the draft pin
 * @property {'place' | 'move' | 'press'} action
 * @property {MapPoint} point
 * @property {string | null} place   the place or unit the pin stands on, when it stands on one
 * @property {ScreenPoint} screen
 */

/**
 * The value of the three tour requests.
 * @typedef {Object} TourPreviewState
 * @property {boolean} playing
 * @property {string | null} tour   the id of the document being played ('' for one that has no id yet)
 * @property {string | null} stop   the id of the stop being played
 */

/**
 * The payload of the event `tour-progress`: what the builder needs to mark the stop being played and to run its
 * own progress line (the page sends no message per frame).
 * @typedef {Object} TourProgress
 * @property {string} tour
 * @property {string | null} stop    the stop's id; null with the phase `end`
 * @property {number} index          its position among the stops being played (-1 with `end`)
 * @property {number} count
 * @property {'travel' | 'stay' | 'end'} phase  flying in, staying, or the playback is over
 * @property {number} seconds        how long this phase lasts (0 with `end`)
 * @property {'finished' | 'stopped'} [reason]  with `end`
 */

/**
 * Everything the fine-tune tools can touch, as the map was built (the value of `fineList`, WS-58). Names are plain words.
 * @typedef {Object} FineInventory
 * @property {{ id: string, name: string, group: string, at: [number, number], k: number, b: number, r: number, movable: boolean }[]} lights
 * @property {{ id: string, name: string, at: [number, number], turn: number }[]} buildings
 * @property {{ id: string, name: string, animals: { kind: string, count: number } | null, polygon: [number, number][], drawn: boolean }[]} fences
 * @property {{ id: string, name: string }[]} paths
 * @property {string[]} seasons
 */

/** WS-68 Render Studio: the shapes Google's image model accepts. @typedef {'16:9' | '3:2' | '4:3' | '1:1' | '4:5' | '9:16'} RenderAspect */

/**
 * Where the render's frame lies on the page (the crop of the chosen aspect, centred, as large as fits) and the lens
 * (the value of `renderMode`, WS-68). `frame` is null while render mode is off.
 * @typedef {Object} RenderModeState
 * @property {boolean} on
 * @property {RenderAspect} aspect
 * @property {number} fov                 degrees, the map camera's vertical lens
 * @property {ScreenRect | null} frame    CSS pixels of the frame (the page's own viewport)
 */

/**
 * One building or place in view (WS-68 `renderPasses`).
 * @typedef {Object} RenderBuilding
 * @property {string} key                               its key in the map
 * @property {[number, number, number, number]} box    its box on the frame, 0..1: left, top, right, bottom
 * @property {number} dist                              metres from the camera
 * @property {number} area                              its share of the frame
 */

/**
 * The light of the instant drawn: the map's own season, date, local time and sun; `aurora`: the northern lights are
 * on and can show (the sky is dark).
 * @typedef {Object} RenderScene
 * @property {string} season
 * @property {number} month
 * @property {number} day
 * @property {number} hour
 * @property {number} sunAlt   degrees above the horizon
 * @property {number} sunAz    degrees from north, clockwise
 * @property {boolean} aurora
 * @property {number} heading  compass degrees the camera looks toward (0 = north, 90 = east; looking straight up: the frame's top)
 * @property {{ bearing: number, elevation: number, intensity: number, inView: boolean } | null} auroraAt  the band as the map holds it
 *   now (null when the northern lights are off or cannot show); `inView`: its height is inside the frame and its centre at most about
 *   15 degrees beside it, and the frame shows sky
 * @property {number} sky              the share of the frame that is sky (0..1), read from the class map
 * @property {number | null} skyTo     how far down from the top the rows are mostly sky (a share of the height); null = no such row
 */

/**
 * The three guide images of one instant, pixel-aligned, at the frame's aspect (the value of `renderPasses`, WS-68).
 * Each Blob crosses postMessage by structured clone.
 * @typedef {Object} RenderPasses
 * @property {number} width
 * @property {number} height
 * @property {RenderAspect} aspect
 * @property {Blob} frame     the clean picture (JPEG), trees softened
 * @property {Blob} seg       the class map (PNG, flat colours), the tree zones over the tree-free map
 * @property {Blob} depth     the distance from the camera (JPEG), white = near
 * @property {RenderBuilding[]} buildings
 * @property {RenderScene} scene
 * @property {string[]} warnings
 */

/** VIS-265 Film: the frame's shape, width:height. @typedef {'9:16' | '9:8' | '3:4' | '4:5' | '1:1' | '16:9'} FilmFrame */

/**
 * The northern lights (v6, their own keyframes): `strength` 0..1 (0 = off, 1 = the brightest the map can show),
 * `bearing` (compass degrees) and `elevation` (degrees above the horizon) of the band.
 * @typedef {Object} FilmAurora
 * @property {number} strength
 * @property {number} bearing
 * @property {number} elevation
 */

/**
 * A Film shot (v6, round 7; lazy/filmshot.js says what every field means): the season of the whole video and its
 * keyframes. Numbers, never words: Film's words map onto them.
 * @typedef {Object} FilmShot
 * @property {number} v
 * @property {FilmFrame} frame
 * @property {string | null} season
 * @property {number | null} [trees]  the share of the trees for the whole video, 0..1 (null: the map's own)
 * @property {FilmKey[]} keys
 * @property {{ id: string, from: number, to: number, start: number, speed: number } | null} [ride]
 */

/**
 * A keyframe (v6): at second `t` it sets the camera (`cam`: view, `hold` seconds, `fly` to the next camera key), the
 * focal length (`lens` millimetres), the light (`light`: date, hour; null fields = the map's own), the northern lights
 * (`aurora`), or any of them; each property flows between its own keys. `cam.seat` ("Ride along"): the camera in the shot's
 * ride, in the rider's seat, looking `yaw` degrees right (-180..180) and `pitch` up (-60..60) of the ride's heading.
 * @typedef {Object} FilmKey
 * @property {string} id
 * @property {number} t
 * @property {{ view: { position: number[], target: number[] }, hold: number, fly: { path: string, start: string, arrive: string, lift: number }, seat?: { yaw: number, pitch: number } | null } | null} cam
 * @property {number | null} lens
 * @property {{ date: string | null, hour: number | null } | null} light
 * @property {FilmAurora | null} aurora
 */

/**
 * A click on the map in Film (the event `film-pick`): the place under it (null on bare ground), its name, the point it
 * centres on and its rough radius in metres.
 * @typedef {Object} FilmPick
 * @property {string | null} place
 * @property {string | null} name
 * @property {number[]} center
 * @property {number} size
 */

/**
 * Film has the map, in which frame, where that frame is on the page (the value of `filmMode`).
 * @typedef {Object} FilmModeState
 * @property {boolean} on
 * @property {FilmFrame} frame
 * @property {ScreenRect | null} rect
 * @property {boolean} playing
 */

/**
 * The view now (the value of `filmView` and `filmPlaceView`): local metres, the lens in millimetres (FilmKey), the
 * map's date ('MM-DD'), hour, northern lights and season.
 * @typedef {Object} FilmView
 * @property {number[]} position
 * @property {number[]} target
 * @property {number} lens
 * @property {string} date
 * @property {number} hour
 * @property {FilmAurora} aurora
 * @property {string} season
 * @property {{ yaw: number, pitch: number } | null} [seat]  riding along at the second shown: where it looks
 */

/**
 * Where a shot is (the value of `filmShow` and `filmPlay`): seconds.
 * @typedef {Object} FilmTime
 * @property {number} t
 * @property {number} length
 */

/**
 * A shot playing (the event `film-progress`): seconds; `playing: false` = it reached its end.
 * @typedef {Object} FilmProgress
 * @property {number} t
 * @property {number} length
 * @property {boolean} playing
 */

/**
 * A recording has begun (the value of `filmRecord`): its size, rate and number of frames.
 * @typedef {Object} FilmRecordStart
 * @property {number} width
 * @property {number} height
 * @property {number} fps
 * @property {number} frames
 */

/**
 * A recording going on (the event `film-record`): frames done of all; at the end `finished`, with the MP4 in `video`,
 * or `error` ('cancelled', 'encode-failed', ...).
 * @typedef {Object} FilmRecordProgress
 * @property {number} done
 * @property {number} frames
 * @property {boolean} finished
 * @property {Blob | null} video
 * @property {string | null} error
 */

/**
 * The clean picture of one instant in the frame (the value of `filmPhoto`).
 * @typedef {Object} FilmPhoto
 * @property {number} width
 * @property {number} height
 * @property {FilmFrame} frame
 * @property {Blob} photo
 */

/**
 * (WS-71 Life) An area things wander in: a package roam area (a key of resort.animalAreas, `src` 'package'), a paddock's
 * own outline (`paddock:<pid>`, `src` 'paddock', not editable in Life) or one drawn or loaded from a GPS file (`a-<6>`).
 * `polygon` = its outline now (with the document shown), `raw` = a GPS file's own points, `m2` = the free walkable area
 * (buildings, props, water and fenced paddocks taken out).
 * @typedef {Object} FineArea
 * @property {string} id
 * @property {string} name
 * @property {[number, number][]} polygon
 * @property {'package' | 'paddock' | 'draw' | 'gps'} src
 * @property {boolean} editable
 * @property {boolean} touched
 * @property {[number, number][] | null} raw
 * @property {boolean} confirm
 * @property {number} m2
 */
/**
 * (WS-71 Life) A paddock's herd: `area` = the area it walks in now ('paddock:<id>' = its own pen), `packageArea` = the
 * same without the touch (in the season shown).
 * @typedef {Object} FineHerd
 * @property {string} id
 * @property {string} name
 * @property {string} kind
 * @property {number} count
 * @property {string} area
 * @property {string} packageArea
 */
/**
 * (WS-71 Life) A group of guests walking about in an area: the package's entry (resort.life.wander) with its touch laid
 * over, or one the panel added. Defaults: count 4, speed 1.2 m/s, pause 8 s, seasons null (every season). `lost` = its
 * area does not resolve (it is not on the map).
 * @typedef {Object} FineWander
 * @property {string} id
 * @property {string} name
 * @property {string} area
 * @property {number} count
 * @property {number} speed
 * @property {number} pause
 * @property {string[] | null} seasons
 * @property {'package' | 'touch'} source
 * @property {boolean} off
 * @property {boolean} confirm
 * @property {boolean} lost
 */
/**
 * (WS-71 Life, piece 3) A sledding hill: the package's entry (resort.life.sled) with its touch laid over, or one the
 * panel added (`s-<6 chars>`). `line` = local metres, the FIRST point is the top. Defaults: count 2 (1..12), walkBack
 * true, speed 5 m/s (2..10, the top slide speed), seasons ['winter'] (null = every season). `top` / `bottom` = the
 * ground height (m) at the first / last point; `length` = along the drawn line (m); `uphill` = the first point is
 * lower than the last (the panel offers to swap them). Its touch: life.sled[id] = { name?, line?, count?, walkBack?,
 * speed?, seasons?, off? } (FineSledTouch in bridge.d.ts).
 * @typedef {Object} FineSled
 * @property {string} id
 * @property {string} name
 * @property {[number, number][]} line
 * @property {number} count
 * @property {boolean} walkBack
 * @property {number} speed
 * @property {string[] | null} seasons
 * @property {'package' | 'touch'} source
 * @property {boolean} off
 * @property {boolean} confirm
 * @property {number} top
 * @property {number} bottom
 * @property {number} length
 * @property {boolean} uphill
 */
/**
 * (WS-71 Life, piece 4) One end of a pair going there and back: `place` = a content place's key (null for a point),
 * `at` = where the end is on the map now (a point wins over a place), `title` = the place's title, else ''.
 * @typedef {Object} FineBetweenEnd
 * @property {string | null} place
 * @property {[number, number]} at
 * @property {string} title
 */
/**
 * (WS-71 Life, piece 4) A pair going there and back between two ends (e.g. reception and a cabin): the package's entry
 * (resort.life.between) with its touch laid over, or one the panel added (`b-<6 chars>`). Walkers go A -> B along the
 * map's own ways (roads, paths, trails, drawn paths), pause, come back, pause, again. Defaults: count 2 (1..12), pause
 * 20 s (0..300), speed 1.2 m/s (0.4..2.5), seasons null (every season). `connected` false = no way joins the two: they
 * walk straight across; `length` = metres along the way. Its touch: life.between[id] = { name?, a?: { place?, at? },
 * b?, count?, pause?, speed?, seasons?, off? } (FineBetweenTouch in bridge.d.ts); a new one carries both ends.
 * @typedef {Object} FineBetween
 * @property {string} id
 * @property {string} name
 * @property {FineBetweenEnd} a
 * @property {FineBetweenEnd} b
 * @property {number} count
 * @property {number} pause
 * @property {number} speed
 * @property {string[] | null} seasons
 * @property {'package' | 'touch'} source
 * @property {boolean} off
 * @property {boolean} confirm
 * @property {boolean} connected
 * @property {number} length
 */
/**
 * (WS-71 Life) A place of the map (its card's title, where its label stands), for pieces 3-4 (there and back).
 * @typedef {Object} FinePlace
 * @property {string} key
 * @property {string} title
 * @property {[number, number]} at
 */
/**
 * (WS-69 Life; VIS-241: the full shape is FineLife in apps/portal/src/editor/bridge.d.ts: + line, kind, built, why,
 * length on each ride, + walk) The rides Fine-tune's Life tool can touch (the value of `fineLife`). Local metres; routes are the points

 * a person drags (a dense package route simplified to about 1.5 m); `direction` = the way the teams go round the route
 * now, 'cw' | 'ccw' seen from above (north up), after any direction the document sets; null for an open route.
 * (WS-71) + the areas, the herds, the guests walking about and the places; (piece 3) + the sledding hills; (piece 4) +
 * the pairs going there and back.
 * @typedef {Object} FineLife
 * @property {{ lat: number, lon: number }} site  the map's origin (a GPS track is placed by it: tools/build_scene.py's projection)
 * @property {{ id: string, name: string, route: { path: [number, number][], closed: boolean }, routes: Object<string, { path: [number, number][], closed: boolean, direction?: string }>, speed: number, dwell: number, direction: 'cw' | 'ccw' | null, trailWidth: number, active: boolean }[]} rides
 * @property {FineArea[]} areas
 * @property {FineHerd[]} herds
 * @property {FineWander[]} wander
 * @property {FineSled[]} sled
 * @property {FineBetween[]} between
 * @property {FinePlace[]} places
 * @property {FineActivity[]} [activities]  (Places and activities) every activity and what moves in it
 */
/**
 * (Places and activities) An activity of the map (a place with `activity`: content.json's, or touches `places`) and
 * its movers, with the document shown (lazy/pathrides.js activitiesOf). Mirrored in apps/portal/src/editor/bridge.d.ts.
 * @typedef {Object} FineActivity
 * @property {string} key       its `places` key, or the package place's key
 * @property {string} name      its card's title now ('' none)
 * @property {'package' | 'touch'} source
 * @property {'follow' | 'fly'} tap   a guest's tap follows what moves (default) or only flies there (the client's content overlay `activity.tap` wins)
 * @property {[number, number] | null} pin   where its pin stands now (`at`, else its first mover's start)
 * @property {{ id: string, pick: string, kind: string, line: string | null, lineName: string, source: 'package' | 'touch', why?: string }[]} movers
 *   added rides, walkers, package rides, package tracks; `pick` = `ride:<id>` | `walk:<id>` | `track:<id>`; `kind` = what
 *   moves (a ride kind, or the walkers' own kind); `why` = not on the map now (FineRide `why`, or `lost`)
 */

/** The requests of the tools (delivery 4), beside the runtime mirror's and editor mode's. WS-58: + the six of fine-tune; WS-69: + `fineLife`; WS-68: + the two of the render studio. */
export const TOOL_REQUESTS = Object.freeze(['drawStart', 'drawVertex', 'drawUndo', 'drawFinish', 'drawCancel', 'drawEdit', 'selectFeature', 'notePins', 'noteDraft', 'hereMarker', 'tourPreview', 'tourPreviewStop', 'tourStop', 'tourTextSample',
  'fineTune', 'fineTouches', 'fineList', 'fineSelect', 'fineBrush', 'fineArm', 'fineMade', 'fineLife', 'fineGroup', 'fineRoute', 'renderMode', 'renderPasses',
  'filmMode', 'filmView', 'filmPlaceView', 'filmAim', 'filmRides', 'filmShow', 'filmPlay', 'filmStop', 'filmPhoto', 'filmRecord']); // (WS-80 B: + fineGroup; WS-84: + fineMade; VIS-265: + the ten of Film)
/** The events of the tools (delivery 4). WS-58: + the three of fine-tune, and `fine-menu` (a right-click on a light). WS-68: + `render-view` (the wheel changed the lens). */
export const TOOL_EVENTS = Object.freeze(['draw', 'draw-end', 'feature-select', 'note-pin', 'here-move', 'tour-progress', 'fine-touches', 'fine-select', 'fine-arm', 'fine-menu', 'fine-remove', 'fine-made-draft', 'render-view', 'film-progress', 'film-pick', 'film-record']); // (fine-remove: Delete on a building asks first; WS-84: + fine-made-draft; WS-91: + here-move; VIS-265: + film-progress, film-pick, film-record)
const IN_PREVIEW = ['hereMarker', 'tourPreview', 'tourPreviewStop', 'tourStop']; // the tool requests "Preview as visitor" still answers
// THE FIELDS OF EACH TOOL REQUEST, as the table above gives them. NO FIELD MAY BE CALLED `mms`, `type` OR `id`: those
// are the envelope's, the editor sends `{ mms: 1, type, id, ...fields }`, and a field of that name would replace the
// request's own id, so its answer would never be matched (found in delivery 4: the feature of `drawEdit` and
// `selectFeature` was `id`; it is `feature`, as in `locate`). A request whose fields break the rule is not
// registered: it answers `not-implemented` and is left out of `hello` (tests/unit/runtime/bridge.test.mjs).
export const TOOL_FIELDS = Object.freeze({
  drawStart: ['kind', 'style'], drawVertex: ['point', 'center', 'index', 'remove'], drawUndo: [], drawFinish: [], drawCancel: [], drawEdit: ['feature', 'geom', 'style'],
  selectFeature: ['feature', 'fly'], notePins: ['pins', 'selected'], noteDraft: ['point', 'arm'], hereMarker: ['place', 'point', 'edit', 'look'],
  tourPreview: ['tour', 'from'], tourPreviewStop: ['tour', 'stop', 'hold', 'look'], tourStop: [], tourTextSample: ['on', 'caption', 'look'],
  fineTune: ['on', 'tool', 'view'], fineTouches: ['touches', 'compare'], fineList: [], fineSelect: ['ids', 'fly'], fineBrush: ['size', 'hide', 'soft', 'mode', 'thick'], fineArm: ['what', 'kind', 'ride', 'finish', 'made', 'undo'], // WS-58 (WS-69: + `ride`; WS-84: + `made`, `undo`; WS-89: fineTune + `view`)
  fineMade: [], // WS-84: the made fences resolved
  fineLife: [], // WS-69 Life
  fineGroup: ['kinds', 'turn', 'move'], // WS-80 B: the Select tool
  fineRoute: ['from', 'to'], // walking directions tried in the editor
  renderMode: ['on', 'aspect', 'fov'], renderPasses: ['width'], // WS-68
  filmMode: ['on', 'frame'], filmView: [], filmPlaceView: ['place'], filmAim: ['place'], filmRides: [], filmShow: ['shot', 't'], filmPlay: ['shot', 'from'], filmStop: [], filmPhoto: ['width'], filmRecord: ['shot', 'fps', 'size', 'blur', 'from', 'to'], // VIS-265
});
/** The fields of a request that the envelope would swallow (none = the request may be registered). */
export const envelopeClash = (fields) => (Array.isArray(fields) ? fields : ['id']).filter((f) => ['mms', 'type', 'id'].includes(f)); // no list at all = not typed = refused
// ---------------------------------------------------------------------------------------------------------------

const EVENTS = ['ready', 'change', 'rebuild-needed', 'select', 'tour'];
const RUNTIME_REQUESTS = ['get', 'set', 'loadPreset', 'controls', 'capture'];
const PICK = ['off', 'place', 'point', 'feature', 'any'];
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/** The allowlist as exact origins: https only, or http on a local development host. Anything else is dropped. */
export function allowedOrigins(list) {
  const out = new Set();
  for (const s of Array.isArray(list) ? list : []) {
    try {
      const u = new URL(s);
      if (u.origin === s.replace(/\/$/, '') && !u.hostname.includes('*') && (u.protocol === 'https:' || (u.protocol === 'http:' && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(u.hostname)))) out.add(u.origin);
    } catch { /* errors:ignore: not an origin */ }
  }
  return out;
}

// ---- WS-51 `reload` (begin): what a reload of the editor frame keeps, in the page's own session storage. Written by
// the bridge alone (so only on a page framed by an allowed origin), read once by config.js before anything is built
// and deleted in the same step. A record of another map or preset, an old one, or anything unreadable is not used.
export const RELOAD_KEY = 'mms-editor-reload';
const RELOAD_AGE = 120000; // ms: a reload takes seconds; an older record belongs to a page that never came back
/** An EditorState from anything: unknown fields dropped; picking is never armed by a stored or a foreign value. */
const editorState = (s) => ({ editor: s?.editor === true, preview: s?.preview === true, ghosts: s?.ghosts === true, pick: 'off' });
/** @param {Storage | null | undefined} storage  @param {object} record  @returns {boolean} false = nothing was kept (storage blocked) */
export function saveReload(storage, record, now = Date.now()) {
  try { storage.setItem(RELOAD_KEY, JSON.stringify({ ...record, v: 1, at: now })); return true; } catch { return false; } // errors:ignore: storage blocked or full; the caller says so
}
/**
 * The record of the reload this page comes from, or null. Reading it deletes it: it is restored once.
 * @param {Storage | null | undefined} storage
 * @param {{ map: string, preset: string, now?: number }} page  the map and the preset the boot record pins
 */
export function takeReload(storage, { map, preset, now = Date.now() }) {
  let rec;
  try {
    const raw = storage.getItem(RELOAD_KEY);
    if (raw === null) return null;
    storage.removeItem(RELOAD_KEY);
    rec = JSON.parse(raw);
  } catch { return null; } // errors:ignore: storage blocked, or not a record
  if (!isObj(rec) || rec.v !== 1 || rec.map !== map || rec.preset !== preset || !(now - rec.at >= 0 && now - rec.at <= RELOAD_AGE)) return null;
  const plain = (o) => (isObj(o) ? o : {});
  const view = isObj(rec.view) && [rec.view.position, rec.view.target].every((p) => Array.isArray(p) && p.length === 3 && p.every(Number.isFinite)) ? rec.view : null;
  return {
    state: editorState(rec.state), view, selection: typeof rec.selection === 'string' ? rec.selection : null,
    layers: { visitor: plain(rec.layers?.visitor), editor: plain(rec.layers?.editor), preset: isObj(rec.layers?.preset) ? rec.layers.preset : null },
  };
}
// ---- WS-51 `reload` (end)

/**
 * The engine side of the editor messages: what main.js hands the bridge (`attach`). The bridge keeps the editor
 * state and the protocol; the host does what only the page can do. A function the host lacks makes its message
 * `not-implemented`.
 * @typedef {Object} EditorHost
 * @property {() => { map: string, preset: string, resort: string }} hello
 * @property {(state: EditorState) => void} state   the editor state changed (also called once at `attach`): ghosts, outlines and picking follow it
 * @property {(key: string | null, o: { fly: boolean, card: boolean }) => string | null} select  answers the key that is selected now
 * @property {(o: { place?: string, point?: MapPoint, feature?: string }) => { screen: ScreenPoint | null, visible: boolean }} locate  (`feature`: delivery 4)
 * @property {(target: HighlightTarget | null) => { rect: ScreenRect | null }} highlight
 * @property {() => ControlRect[]} listControls
 * @property {() => PlaceInfo[]} places
 * @property {() => string | null} selection
 * @property {(spec: StillSpec) => Promise<{ blob: Blob, width: number, height: number, type: string }>} still
 * @property {(keep: { view: boolean, time: boolean, selection: boolean }) => object} keep  the reload record: `{ map, preset, layers, view, selection }`
 * @property {() => void} reload   reloads the page (called once the record is stored and the answer is posted)
 * @property {(event: string, fn: (payload: any) => void) => (() => void)} on   EDITOR_EVENTS and, since delivery 4, TOOL_EVENTS
 * @property {{ [request: string]: (fields: object) => any }} [tools]  delivery 4: one function per TOOL_REQUESTS name (the fields of the message in, the value or a promise of it out)
 */
// The editor sends its first `hello` when the frame has loaded, which is before the map is built (and before main.js
// has run at all: it waits for the package). config.js calls this while the modules are evaluated, so a listener
// is there by then; what arrives is kept as it came and judged by createBridge, which takes the listening over.
let early = null;
/** @param {string[]} origins the boot record's `editor` list  @param {Window} [win] */
export function listenEarly(origins, win = window) {
  if (early || !allowedOrigins(origins).size || !win.parent || win.parent === win || typeof win.addEventListener !== 'function') return;
  const inbox = [], keep = (e) => { if (inbox.length < 50) inbox.push(e); };
  win.addEventListener('message', keep);
  early = { win, inbox, stop: () => win.removeEventListener('message', keep) };
}

// the host function each editor message needs; `hello` lists the messages whose function is there
const NEEDS = { hello: 'hello', enterEditor: 'state', leaveEditor: 'state', visitorPreview: 'state', pick: 'state', select: 'select', locate: 'locate', highlight: 'highlight', listControls: 'listControls', places: 'places', reload: 'reload' };

/**
 * @param {object} o
 * @param {Runtime} o.runtime
 * @param {string[]} o.origins  the allowlist from the boot record
 * @param {Window} [o.win]
 * @param {EditorHost | null} [o.editor]  the engine side of the editor messages; without it they answer `not-implemented`
 * @param {boolean} [o.defer]  true: requests wait for `attach(host)`. main.js listens from the start of the load, so
 *   the editor's first `hello` is not lost, and answers once the map is built
 * @param {{ state?: object } | null} [o.restored]  this load comes from `reload` (config.js, `takeReload`)
 * @param {{ [request: string]: string[] }} [o.toolFields]  the typed fields of the tool requests (TOOL_FIELDS; a test hands in another list)
 * @returns {{ dispose(): void, attach(host: EditorHost): void, state(): EditorState } | null}
 */
export function createBridge({ runtime, origins, win = window, editor = null, defer = false, restored = null, toolFields = TOOL_FIELDS }) {
  const allowed = allowedOrigins(origins), parent = win.parent;
  if (!allowed.size || !parent || parent === win) return null; // a public page, or a page nobody frames: no editor mode, no listener
  let peer = null; // the allowed origin that spoke; answers and events go to it alone
  let host = null, has = new Set(), queue = defer ? [] : null, once = false, ready = null, told = false, offs = [];
  const state = editorState(restored?.state);
  const snap = () => ({ ...state });
  const push = () => { host.state(snap()); return snap(); };
  const post = (msg) => {
    if (!peer) return;
    try { parent.postMessage({ mms: 1, ...msg }, peer); } catch (err) { console.warn('bridge: message not sent', err); }
  };
  const event = (name, payload) => post({ type: 'event', id: '', event: name, payload });
  const calls = {
    get: (m) => runtime.get(m.key),
    set: (m) => runtime.set(m.patch, { source: m.source, ms: m.ms }),
    loadPreset: (m) => runtime.loadPreset(m.doc),
    controls: () => runtime.controls(),
    // the runtime's { view, values }, the selection and, when asked, a still of the map (a Blob: the answer waits for it)
    capture: (m) => {
      const out = host?.selection ? { ...runtime.capture(), selection: host.selection() } : runtime.capture();
      if (!isObj(m.still)) return out;
      if (!host?.still) throw new Error('not-implemented');
      if (m.still.view !== undefined && !isObj(m.still.view)) throw new Error('bad-view'); // the page checks its numbers (main.js still)
      const px = (n) => Math.round(Math.min(2048, Math.max(16, Number(n) || 0)));
      return host.still({ ...m.still, width: px(m.still.width), height: px(m.still.height) }).then((still) => ({ ...out, still }));
    },
    hello: () => ({ protocol: 2, ...host.hello(), implements: [...RUNTIME_REQUESTS, ...EDITOR_REQUESTS.filter((t) => has.has(t)), ...TOOL_REQUESTS.filter((t) => has.has(t))], editor: snap() }),
    enterEditor: (m) => { state.editor = true; if (typeof m.ghosts === 'boolean') state.ghosts = m.ghosts; if (PICK.includes(m.pick)) { state.pick = m.pick; once = false; } return push(); },
    leaveEditor: () => { Object.assign(state, editorState(null)); once = false; host.highlight?.(null); return push(); },
    visitorPreview: (m) => { state.preview = m.on === true; return push(); },
    pick: (m) => { if (!PICK.includes(m.mode)) throw new Error('bad-mode'); if (m.mode === 'feature' && !has.has('selectFeature')) throw new Error('not-implemented'); state.pick = m.mode; once = m.once === true && m.mode !== 'off'; push(); return { mode: state.pick }; },
    select: (m) => ({ key: host.select(typeof m.key === 'string' ? m.key : null, { fly: m.fly === true, card: m.card === true }) }),
    locate: (m) => host.locate({ place: typeof m.place === 'string' ? m.place : undefined, point: isObj(m.point) ? m.point : undefined, feature: typeof m.feature === 'string' ? m.feature : undefined }),
    highlight: (m) => host.highlight(isObj(m.target) ? m.target : null),
    listControls: () => host.listControls(),
    places: () => host.places(),
    reload: (m) => {
      if (typeof host.keep !== 'function') throw new Error('not-implemented');
      const keep = { view: true, time: true, selection: true, ...(isObj(m.keep) ? m.keep : {}) };
      if (!saveReload(win.sessionStorage, { ...host.keep(keep), state: snap() })) throw new Error('no-storage');
      win.setTimeout(() => host.reload(), 0); // after this answer has been posted
      return { reloading: true };
    },
  };
  // WS-52: a tool request is the host's function of the same name, asked with the fields of the message
  for (const type of TOOL_REQUESTS) calls[type] = ({ mms: _m, type: _t, id: _i, ...fields }) => {
    if (!state.editor || (state.preview && !IN_PREVIEW.includes(type))) throw new Error('not-editing');
    return host.tools[type](fields);
  };
  const answer = (m) => {
    const ok = (value) => post({ type: 'result', id: m.id, value }), no = (err) => post({ type: 'error', id: m.id, message: String(err?.message || err) });
    if (!RUNTIME_REQUESTS.includes(m.type) && !has.has(m.type)) no('not-implemented'); // typed, and this page has no body for it
    else try { const v = calls[m.type](m); if (typeof v?.then === 'function') v.then(ok, no); else ok(v); } catch (err) { no(err); }
    if (ready && !told) { told = true; event('ready', ready); } // the map was ready before the editor first spoke: it hears it now
  };
  const onMessage = (e) => {
    const m = e.data;
    if (e.source !== parent || !allowed.has(e.origin) || !m || m.mms !== 1 || typeof m.id !== 'string') return;
    if (!RUNTIME_REQUESTS.includes(m.type) && !EDITOR_REQUESTS.includes(m.type) && !TOOL_REQUESTS.includes(m.type)) return; // not a message of the interface: unanswered, as before
    peer = e.origin;
    if (queue) queue.push(m); else answer(m);
  };
  win.addEventListener('message', onMessage);
  const before = early?.win === win ? early : null; // what arrived before this point (listenEarly)
  if (before) { before.stop(); early = null; }
  const off = EVENTS.map((name) => runtime.on(name, (payload) => {
    if (name !== 'ready') return event(name, payload);
    ready = { ...payload, editor: snap(), restored: !!restored }; // WS-51: with the editor state, and whether this load came from `reload`
    if (peer && !queue) { told = true; event('ready', ready); }
  }));
  function attach(h) {
    host = h;
    has = new Set([...Object.keys(NEEDS).filter((type) => typeof h?.[NEEDS[type]] === 'function'), ...TOOL_REQUESTS.filter((type) => typeof h?.tools?.[type] === 'function' && !envelopeClash(toolFields[type]).length)]);
    offs = typeof h?.on !== 'function' ? [] : [...EDITOR_EVENTS, ...TOOL_EVENTS].map((name) => h.on(name, (payload) => {
      event(name, payload);
      if (name === 'pick' && once) { once = false; state.pick = 'off'; push(); } // `once`: back to `off` after the first press
    }));
    if (has.has('enterEditor')) push(); // the page follows the state it starts with (a restored one after `reload`)
    const waiting = queue || [];
    queue = null;
    for (const m of waiting) answer(m);
  }
  if (editor) attach(editor); else if (!defer) queue = null;
  for (const e of before?.inbox || []) onMessage(e); // judged like any message; answered now, or when the host is attached
  return { attach, state: snap, dispose() { win.removeEventListener('message', onMessage); off.forEach((fn) => fn()); offs.forEach((fn) => fn?.()); } };
}
